import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import CardEditModal from '../components/CardEditModal';
import CardCreateModal from '../components/CardCreateModal';
import { useAuth } from '../contexts/AuthContext';
import { stripEmoji, shortLabel } from '../utils/text';
import {
  SUSPENDED_STATE,
  REACTIVATED_STATE,
  isSuspended as isCardSuspended,
  suspendCard,
  reactivateCard,
} from '../utils/cards';

// Pastille de filtre — pleine quand active, contour sinon.
const Chip = ({ active, onClick, children, tone = 'ink' }) => {
  const base =
    'px-3.5 py-1.5 rounded-full whitespace-nowrap text-sm transition-colors border';
  const styles = active
    ? tone === 'clay'
      ? 'bg-clay-500 text-onAccent border-clay-500'
      : 'bg-ink-900 text-onAccent border-ink-900'
    : 'bg-surface text-ink-700 border-sand-300 hover:border-ink-400';
  return (
    <button onClick={onClick} className={`${base} ${styles}`}>
      {children}
    </button>
  );
};

// SearchPage transformée en Bibliothèque
export const SearchPage = () => {
  const [allCards, setAllCards] = useState([]);
  const [filteredCards, setFilteredCards] = useState([]);
  const [decks, setDecks] = useState([]);
  const [selectedDeck, setSelectedDeck] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [editingCard, setEditingCard] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [showSuspended, setShowSuspended] = useState(false); // Nouveau filtre
  const [pendingCardId, setPendingCardId] = useState(null); // Carte en cours de suspension/réactivation

  const loadAllCards = useCallback(async () => {
    try {
      // Charger TOUTES les cartes (pas seulement celles à réviser)
      const response = await fetch('https://n8n.automationtekleabsam.com/webhook/load-all-cards');
      const data = await response.json();

      if (data.success && data.cards) {
        setAllCards(data.cards);
      }
      setIsLoading(false);
    } catch (error) {
      console.error('Erreur chargement cartes:', error);
      setIsLoading(false);
    }
  }, []);

  const loadDecks = useCallback(async () => {
    try {
      const response = await fetch('https://n8n.automationtekleabsam.com/webhook/load-decks');
      const data = await response.json();

      if (data.success && data.decks) {
        setDecks(data.decks);
      }
    } catch (error) {
      console.error('Erreur chargement decks:', error);
    }
  }, []);

  const filterCards = useCallback(() => {
    let filtered = [...allCards];

    // Filtrer par statut suspendu
    if (showSuspended) {
      filtered = filtered.filter(isCardSuspended);
    } else {
      // Exclure les cartes suspendues en mode normal
      filtered = filtered.filter(card => !isCardSuspended(card));
    }

    // Filtrer par deck
    if (selectedDeck !== 'all') {
      filtered = filtered.filter(card => {
        const cardDeckId = (card.deck || '').toLowerCase().replace(/[^a-z0-9]/g, '-');
        return cardDeckId === selectedDeck;
      });
    }

    // Filtrer par recherche
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(card =>
        (card.name || '').toLowerCase().includes(query) ||
        (card.textFrench || '').toLowerCase().includes(query) ||
        (card.translationTigrinya || '').toLowerCase().includes(query) ||
        (card.pronunciation || '').toLowerCase().includes(query) ||
        (card.context || '').toLowerCase().includes(query)
      );
    }

    setFilteredCards(filtered);
  }, [allCards, searchQuery, selectedDeck, showSuspended]);

  // Charger toutes les cartes et les decks au démarrage
  useEffect(() => {
    loadAllCards();
    loadDecks();
  }, [loadAllCards, loadDecks]);

  // Filtrer les cartes quand la recherche ou le deck change
  useEffect(() => {
    filterCards();
  }, [filterCards]);

  const handleEditCard = (card) => {
    setEditingCard(card);
    setIsModalOpen(true);
  };

  const handleSaveCard = (updatedCard) => {
    // Mettre à jour dans la liste locale
    setAllCards(prev =>
      prev.map(c => c.pageId === updatedCard.pageId ? updatedCard : c)
    );
    setFilteredCards(prev =>
      prev.map(c => c.pageId === updatedCard.pageId ? updatedCard : c)
    );
  };

  // Notion est la source de vérité, mais l'affichage n'a pas à attendre un
  // rechargement complet de la bibliothèque : on reflète le nouvel état
  // localement, ce qui fait sortir la carte du filtre courant immédiatement.
  const applyState = (pageId, state) => {
    setAllCards(prev =>
      prev.map(c => (c.pageId === pageId ? { ...c, state } : c))
    );
  };

  const handleSuspendCard = async (card) => {
    if (pendingCardId) return;

    if (!window.confirm(
      'Suspendre cette carte ?\n\n' +
      'Elle sort de la révision et se retrouve ici sous le filtre « Suspendues ».'
    )) {
      return;
    }

    setPendingCardId(card.pageId);
    try {
      await suspendCard(card.pageId);
      applyState(card.pageId, SUSPENDED_STATE);
    } catch (error) {
      console.error('Erreur suspension carte:', error);
      alert('Erreur lors de la suspension de la carte.');
    } finally {
      setPendingCardId(null);
    }
  };

  const handleReactivateCard = async (card) => {
    if (pendingCardId) return;

    if (!window.confirm('Réactiver cette carte ?\n\nElle réintègre la révision à sa prochaine échéance.')) {
      return;
    }

    setPendingCardId(card.pageId);
    try {
      await reactivateCard(card.pageId);
      applyState(card.pageId, REACTIVATED_STATE);
    } catch (error) {
      console.error('Erreur réactivation carte:', error);
      alert('Erreur lors de la réactivation de la carte.');
    } finally {
      setPendingCardId(null);
    }
  };

  const handleCreateCard = (newCard) => {
    // Ajouter la nouvelle carte aux listes
    setAllCards(prev => [newCard, ...prev]);
    setFilteredCards(prev => [newCard, ...prev]);
    setIsCreateModalOpen(false);

    // Recharger les cartes pour avoir les données à jour depuis Notion
    setTimeout(() => {
      loadAllCards();
    }, 1000);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen pb-28 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 mx-auto mb-4 rounded-full border-2 border-sand-300 border-t-clay-500 animate-spin" />
          <div className="label-caps">Chargement de la bibliothèque</div>
        </div>
      </div>
    );
  }

  const suspendedCount = allCards.filter(isCardSuspended).length;
  const activeCount = allCards.length - suspendedCount;

  return (
    <div className="min-h-screen pb-28">
      {/* En-tête : recherche et filtres restent accessibles au défilement */}
      <header className="bg-sand-50/95 backdrop-blur border-b border-sand-200 sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-5 pt-8 pb-3">
          <div className="flex items-baseline justify-between gap-4">
            <div>
              <h1 className="font-display text-[2rem] leading-none text-ink-900">
                Bibliothèque
              </h1>
              <p className="label-caps mt-2">{allCards.length} cartes</p>
            </div>
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="shrink-0 px-4 py-2 bg-clay-500 text-onAccent rounded-xl text-sm font-medium
                         hover:bg-clay-600 transition-colors"
            >
              Nouvelle carte
            </button>
          </div>

          {/* Recherche */}
          <div className="relative mt-5">
            <svg
              className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400"
              fill="none" stroke="currentColor" strokeWidth={1.75} viewBox="0 0 24 24"
            >
              <circle cx="11" cy="11" r="7" />
              <path strokeLinecap="round" d="m20 20-3.5-3.5" />
            </svg>
            <input
              type="text"
              placeholder="Rechercher une carte…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-9 py-2.5 bg-surface border border-sand-300 rounded-xl
                         text-ink-900 placeholder:text-ink-400 outline-none transition-colors
                         focus:border-clay-500 focus:ring-2 focus:ring-clay-500/20"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-400 hover:text-ink-900"
                aria-label="Effacer la recherche"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.75} viewBox="0 0 24 24">
                  <path strokeLinecap="round" d="M6 6l12 12M18 6L6 18" />
                </svg>
              </button>
            )}
          </div>

          {/* Filtres */}
          <div className="flex gap-2 overflow-x-auto pb-1 mt-3 -mx-5 px-5">
            <Chip
              tone="clay"
              active={showSuspended}
              onClick={() => {
                setShowSuspended(!showSuspended);
                setSelectedDeck('all');
              }}
            >
              Suspendues · {suspendedCount}
            </Chip>

            {!showSuspended && (
              <>
                <span className="self-center w-px h-5 bg-sand-300 shrink-0" />
                <Chip active={selectedDeck === 'all'} onClick={() => setSelectedDeck('all')}>
                  Toutes · {activeCount}
                </Chip>
                {decks.map((deck) => (
                  <Chip
                    key={deck.id}
                    active={selectedDeck === deck.id}
                    onClick={() => setSelectedDeck(deck.id)}
                  >
                    {stripEmoji(deck.name)} · {deck.totalCards || 0}
                  </Chip>
                ))}
              </>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-5">
        <p className="label-caps py-5">
          {filteredCards.length} carte{filteredCards.length > 1 ? 's' : ''}
          {searchQuery && ` · « ${searchQuery} »`}
          {!showSuspended && selectedDeck !== 'all' &&
            ` · ${stripEmoji(decks.find(d => d.id === selectedDeck)?.name)}`}
        </p>

        {filteredCards.length === 0 ? (
          <div className="py-16 text-center max-w-sm mx-auto">
            <h2 className="font-display text-2xl text-ink-900 mb-3">
              Aucune carte
            </h2>
            <p className="text-ink-500 leading-relaxed">
              {searchQuery
                ? 'Aucun résultat pour cette recherche.'
                : 'Ce deck ne contient encore aucune carte.'}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-sand-200 border-t border-sand-200">
            {filteredCards.map((card) => {
              const isSuspended = isCardSuspended(card);
              const isPending = pendingCardId === card.pageId;

              return (
                <li
                  key={card.pageId}
                  className={`py-5 ${isSuspended ? 'pl-4 border-l-2 border-rating-hard' : ''}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      {/* Provenance de la carte */}
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        <span className="label-caps text-clay-600">
                          {stripEmoji(card.deck) || 'Sans deck'}
                        </span>
                        <span className="text-sand-300">·</span>
                        <span className="label-caps">
                          {shortLabel(card.level) || 'Nouveau'}
                        </span>
                        {isSuspended && (
                          <>
                            <span className="text-sand-300">·</span>
                            <span className="label-caps text-rating-hard">Suspendue</span>
                          </>
                        )}
                      </div>

                      <h3 className="font-display text-xl text-ink-900 leading-snug">
                        {card.name || <span className="text-ink-400">Carte sans texte</span>}
                      </h3>
                      {card.textFrench && (
                        <p className="text-sm text-ink-500 mt-0.5">{card.textFrench}</p>
                      )}

                      {card.translationTigrinya && (
                        <p className="font-geez text-2xl text-ink-900 mt-2.5">
                          {card.translationTigrinya}
                        </p>
                      )}
                      {card.pronunciation && (
                        <p className="label-caps mt-1.5">{card.pronunciation}</p>
                      )}
                      {card.context && (
                        <p className="font-display italic text-ink-700 mt-3 leading-relaxed">
                          {card.context}
                        </p>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col gap-1 shrink-0">
                      {isSuspended ? (
                        <button
                          onClick={() => handleReactivateCard(card)}
                          disabled={isPending}
                          className="p-2 text-ink-400 hover:text-rating-easy disabled:text-sand-300 transition-colors"
                          title="Réactiver la carte"
                          aria-label="Réactiver la carte"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.75} viewBox="0 0 24 24">
                            <circle cx="12" cy="12" r="9" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="m10.5 9 5 3-5 3z" />
                          </svg>
                        </button>
                      ) : (
                        <button
                          onClick={() => handleSuspendCard(card)}
                          disabled={isPending}
                          className="p-2 text-ink-400 hover:text-rating-hard disabled:text-sand-300 transition-colors"
                          title="Suspendre la carte"
                          aria-label="Suspendre la carte"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.75} viewBox="0 0 24 24">
                            <circle cx="12" cy="12" r="9" />
                            <path strokeLinecap="round" d="M10 9.5v5M14 9.5v5" />
                          </svg>
                        </button>
                      )}
                      <button
                        onClick={() => handleEditCard(card)}
                        className="p-2 text-ink-400 hover:text-clay-600 transition-colors"
                        title="Éditer la carte"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.75} viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Historique de la carte */}
                  <div className="flex gap-5 mt-4 label-caps">
                    <span>{card.reviewCount || 0} révisions</span>
                    <span>{card.successRate || 0}% réussite</span>
                    <span>intervalle {card.interval || 1} j</span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </main>

      {/* Modal d'édition */}
      <CardEditModal
        card={editingCard}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveCard}
      />

      {/* Modal de création */}
      <CardCreateModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSave={handleCreateCard}
      />
    </div>
  );
};

export const AccountPage = () => {
  const navigate = useNavigate();
  const { logout, user, can } = useAuth();

  const roleLabel = user?.role === 'admin' ? 'Administrateur' : 'Contributeur';

  const handleLogout = () => {
    if (window.confirm('Se déconnecter ?')) {
      logout();
      navigate('/login');
    }
  };

  return (
    <div className="min-h-screen pb-28">
      <header className="max-w-2xl mx-auto px-5 pt-10 pb-8">
        <h1 className="font-display text-[2rem] leading-none text-ink-900">
          {user?.displayName || 'Compte'}
        </h1>
        <p className="label-caps mt-2">
          {roleLabel}
          {user?.username && <span className="text-ink-400"> · {user.username}</span>}
        </p>
      </header>

      <main className="max-w-2xl mx-auto px-5">
        <ul className="divide-y divide-sand-200 border-y border-sand-200">
          {can('settings') && (
            <li>
              <button
                onClick={() => navigate('/settings')}
                className="w-full py-4 flex items-center justify-between group"
              >
                <span className="text-ink-900 group-hover:text-clay-600 transition-colors">
                  Paramètres de révision
                </span>
                <svg className="w-4 h-4 text-ink-400" fill="none" stroke="currentColor" strokeWidth={1.75} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m9 5 7 7-7 7" />
                </svg>
              </button>
            </li>
          )}
          <li>
            <button
              onClick={() => navigate('/search')}
              className="w-full py-4 flex items-center justify-between group"
            >
              <span className="text-ink-900 group-hover:text-clay-600 transition-colors">
                Bibliothèque de cartes
              </span>
              <svg className="w-4 h-4 text-ink-400" fill="none" stroke="currentColor" strokeWidth={1.75} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="m9 5 7 7-7 7" />
              </svg>
            </button>
          </li>
        </ul>

        {/* Mode d'emploi des accès — visible du seul administrateur.
            Placé ici volontairement : c'est l'endroit où l'on se trouve
            quand on en a besoin, y compris depuis un téléphone. */}
        {can('settings') && (
          <section className="pt-8 border-t border-sand-200">
            <h2 className="label-caps mb-3">Gestion des accès</h2>
            <p className="text-ink-500 leading-relaxed">
              L'accès se fait en deux temps, et chacun se règle ailleurs.
            </p>

            <div className="mt-5 space-y-3">
              <details className="bg-surface border border-sand-200 rounded-xl">
                <summary className="px-5 py-4 cursor-pointer font-medium text-ink-900">
                  Ajouter une personne
                </summary>
                <div className="px-5 pb-5 text-sm text-ink-500 leading-relaxed space-y-3">
                  <p>
                    <span className="text-ink-900 font-medium">1. Le verrou du site</span>
                    {' — '}dans Dockploy, onglet <em>Environment</em>, ajouter un
                    segment à la variable <code className="text-clay-600">SITE_USERS</code>,
                    puis <em>Redeploy</em>.
                  </p>
                  <p className="font-mono text-xs bg-sand-100 rounded-lg p-3 break-all text-ink-700">
                    samuel:motdepasse;erianna:motdepasse;nouvelle:motdepasse
                  </p>
                  <p>
                    Le point-virgule sépare les personnes, le deux-points sépare
                    l'identifiant du mot de passe. Un mot de passe ne peut donc
                    contenir ni « ; » ni « : ».
                  </p>
                  <p>
                    <span className="text-ink-900 font-medium">2. Le compte applicatif</span>
                    {' — '}dans la base Notion <em>👥 Utilisateurs</em>, ajouter une
                    ligne : identifiant, mot de passe, rôle
                    (<em>admin</em> ou <em>contributeur</em>), et cocher <em>Active</em>.
                    Effet immédiat, sans redéploiement.
                  </p>
                </div>
              </details>

              <details className="bg-surface border border-sand-200 rounded-xl">
                <summary className="px-5 py-4 cursor-pointer font-medium text-ink-900">
                  Mot de passe oublié
                </summary>
                <div className="px-5 pb-5 text-sm text-ink-500 leading-relaxed space-y-3">
                  <p>
                    Il n'y a rien à récupérer : aucun mot de passe n'est conservé
                    en clair. On en réécrit simplement un nouveau, sans avoir à
                    connaître l'ancien.
                  </p>
                  <p>
                    <span className="text-ink-900 font-medium">Verrou du site</span>
                    {' — '}remplacer le mot de passe dans <code className="text-clay-600">SITE_USERS</code>,
                    puis <em>Redeploy</em>.
                  </p>
                  <p>
                    <span className="text-ink-900 font-medium">Compte applicatif</span>
                    {' — '}réécrire la colonne <em>Password</em> dans Notion.
                  </p>
                  <p className="text-ink-400">
                    Tant que vous gardez l'accès à Dockploy et à Notion, vous ne
                    pouvez pas rester enfermé dehors.
                  </p>
                </div>
              </details>

              <details className="bg-surface border border-sand-200 rounded-xl">
                <summary className="px-5 py-4 cursor-pointer font-medium text-ink-900">
                  Retirer un accès
                </summary>
                <div className="px-5 pb-5 text-sm text-ink-500 leading-relaxed space-y-3">
                  <p>
                    <span className="text-ink-900 font-medium">Immédiat</span>
                    {' — '}décocher <em>Active</em> dans la base Notion. La personne
                    ne peut plus se connecter dès sa prochaine tentative.
                  </p>
                  <p>
                    <span className="text-ink-900 font-medium">Complet</span>
                    {' — '}retirer aussi son segment de <code className="text-clay-600">SITE_USERS</code>,
                    sinon elle peut encore charger le site sans pouvoir y entrer.
                  </p>
                </div>
              </details>
            </div>

            <p className="text-xs text-ink-400 mt-4 leading-relaxed">
              Si <code>SITE_USERS</code> n'est pas définie dans Dockploy, ce sont les
              identifiants embarqués dans l'image qui s'appliquent. La variable,
              quand elle existe, l'emporte toujours.
            </p>
          </section>
        )}

        {/* À propos — remplace les entrées de menu qui ne menaient nulle part */}
        <section className="pt-8">
          <h2 className="label-caps mb-3">À propos</h2>
          <p className="text-ink-500 leading-relaxed">
            Vos cartes sont stockées dans Notion et synchronisées par n8n.
            La planification des révisions suit l'algorithme FSRS-4.5 : chaque
            note ajuste la stabilité de la carte, qui détermine la date de sa
            prochaine apparition.
          </p>

          {!can('review') && (
            <p className="text-ink-500 leading-relaxed mt-4">
              Votre compte est un compte contributeur : vous pouvez consulter la
              bibliothèque, créer et corriger des cartes, et utiliser l'assistant.
              La révision et sa progression restent celles de l'administrateur.
            </p>
          )}
        </section>

        <button
          onClick={handleLogout}
          className="w-full mt-10 py-3.5 border border-sand-300 rounded-xl text-ink-700
                     hover:border-rating-forgot hover:text-rating-forgot transition-colors"
        >
          Déconnexion
        </button>
      </main>
    </div>
  );
};
